import { toInteger } from 'lodash';
import { createCanvas, loadImageAsync } from '@/utils/file-kit';
import { type ILayerProps, type ISize, type ITextLayerProps } from '@/version-types';
import { SVGTransformer } from './to-svg';

/**
 * 用于出图时，获取绘制结果 canvas
 */
export class ResultCanvasCreator {
    private layer: ILayerProps;
    private layerData: ITextLayerProps;
    private stageSize: ISize;
    private fontSubsetToBase64: Record<string, string> = {};

    constructor(
        private identity: string,
        layer: ILayerProps,
        stageSize: ISize,
        fontSubsetToBase64: Record<string, string> = {},
    ) {
        this.layer = layer;
        this.layerData = layer.data as ITextLayerProps;
        this.stageSize = stageSize;
        this.fontSubsetToBase64 = fontSubsetToBase64;
    }

    private async getImageSourceByScreenShot(url: string) {
        const { width: layerWidth, height: layerHeight } = this.layer;
        const width = toInteger(layerWidth);
        const height = toInteger(layerHeight);

        const { canvas, ctx } = createCanvas(width, height);
        const image = await loadImageAsync(url);

        // --- 应用透明度 ---
        const { opacity } = this.layer;
        ctx.globalAlpha = opacity;

        // --- 应用背景色 ---
        const { textBackground = [] } = this.layerData;
        if (textBackground.length > 0) {
            const { color, hidden } = textBackground[0];
            if (!hidden) {
                ctx.fillStyle = color;
                ctx.fillRect(0, 0, width, height);
            }
        }

        // --- 应用翻转 ---
        const { flipH, flipV } = this.layer;
        const scaleX = flipH ? -1 : 1;
        const scaleY = flipV ? -1 : 1;
        ctx.scale(scaleX, scaleY);

        // --- 绘制 ---
        ctx.drawImage(
            image,
            0,
            0,
            image.width,
            image.height,
            scaleX === -1 ? -width : 0,
            scaleY === -1 ? -height : 0,
            width,
            height,
        );

        return {
            source: canvas,
            width,
            height,
        };
    }

    private async getImageSourceBySVGImage() {
        const svgTransformer = new SVGTransformer(this.identity, this.layer, this.fontSubsetToBase64);
        const svgInfo = await svgTransformer.transform();

        if (!svgInfo) return null;

        const { image, width, height } = svgInfo;

        return {
            source: image,
            width,
            height,
        };
    }

    private async getImageSource() {
        const { screenshotUrl } = this.layerData;

        if (screenshotUrl) {
            return this.getImageSourceByScreenShot(screenshotUrl);
        }

        return this.getImageSourceBySVGImage();
    }

    public async create() {
        const imageSource = await this.getImageSource();
        if (!imageSource) return;

        const { source, width, height } = imageSource;
        const { width: layerWidth, height: layerHeight, left, top, rotate } = this.layer;
        const { width: stageWidth, height: stageHeight } = this.stageSize;

        const { canvas, ctx } = createCanvas(stageWidth, stageHeight);

        ctx.translate(toInteger(layerWidth) / 2 + left, toInteger(layerHeight) / 2 + top);
        ctx.rotate(((2 * Math.PI) / 360) * rotate);

        ctx.drawImage(source, 0, 0, width, height, -width / 2, -height / 2, width, height);

        return canvas;
    }
}
